import React from "react";
import { ArrowRight } from "lucide-react";
import { Chip } from "../uploads/FileUploadSource";
import { LAYER, num } from "../pipeline/Layer";
import { BronzeState, LAYERS, NAME, rowsText, scoreTone } from "./ReadinessLayers";
import QualityTrend from "./QualityTrend";
import { cn } from "../../lib/utils";

const ST = { ready: ["Ready", "success"], limited: ["Usable with limits", "warning"], not_ready: ["Not ready", "critical"] };
const th = "px-3 py-2 border-b border-border text-left font-semibold";

const Head = ({ first, extra }) => (
  <thead><tr className="text-[10px] uppercase tracking-widest text-muted"><th className={th}>{first}</th>
    {LAYERS.map(l => <th key={l} className={th}><span className={cn("inline-flex items-center gap-1.5", LAYER[NAME[l]].text)}><span className={cn("w-1.5 h-1.5 rounded-full", LAYER[NAME[l]].dot)} />{NAME[l]}</span></th>)}
    {extra && <th className={th}>{extra}</th>}</tr></thead>
);

function Models({ data }) {
  const base = (data.silver || data.bronze || data.gold)?.models || [];
  const st = (l, id) => data[l]?.models?.find(m => m.id === id)?.status;
  return (
    <div className="tile overflow-x-auto" data-testid="rd-cmp-models">
      <table className="w-full text-[12px] min-w-[560px]"><Head first="Model" />
        <tbody>{base.map(m => (
          <tr key={m.id} className="border-b border-border/60" data-testid={`rd-cmp-model-${m.id}`}>
            <td className="px-3 py-2"><div className="font-medium">{m.label}</div><div className="text-[10px] text-muted">{m.group}</div></td>
            {LAYERS.map(l => <td key={l} className="px-3 py-2" data-testid={`rd-cmp-model-${m.id}-${l}`}>{st(l, m.id) ? <Chip tone={ST[st(l, m.id)][1]}>{ST[st(l, m.id)][0]}</Chip> : <span className="text-muted">—</span>}</td>)}
          </tr>))}</tbody></table>
    </div>
  );
}

function Score({ t, layer }) {
  if (!t) return <span className="text-muted text-[11.5px]">{layer === "gold" ? "Not certified" : "Not in this layer"}</span>;
  return <span className="inline-flex items-baseline gap-1.5"><span className={cn("font-semibold font-mono-tabular text-[13px]", scoreTone(t.score))}>{t.score}</span>
    <span className="text-[10.5px] text-muted font-mono-tabular">{rowsText(t)}</span></span>;
}

function Tables({ data }) {
  const types = [...new Set(LAYERS.flatMap(l => (data[l]?.tables || []).map(t => t.type)))];
  const pick = (l, type) => data[l]?.tables?.find(t => t.type === type);
  const label = (type) => LAYERS.map(l => pick(l, type)?.label).find(Boolean);
  return (
    <div className="tile overflow-x-auto" data-testid="rd-cmp-tables">
      <table className="w-full text-[12px] min-w-[640px]"><Head first="Table · quality score" extra="Bronze → Silver" />
        <tbody>{types.map(type => {
          const b = pick("bronze", type), s = pick("silver", type);
          const d = b && s ? s.score - b.score : null;
          return (
            <tr key={type} className="border-b border-border/60" data-testid={`rd-cmp-table-${type}`}>
              <td className="px-3 py-2 font-medium">{label(type)}</td>
              {LAYERS.map(l => <td key={l} className="px-3 py-2" data-testid={`rd-cmp-table-${type}-${l}`}><Score t={pick(l, type)} layer={l} /></td>)}
              <td className="px-3 py-2 font-mono-tabular" data-testid={`rd-cmp-table-${type}-delta`}>{d == null ? <span className="text-muted">—</span>
                : <span className={cn("inline-flex items-center gap-1", d > 0 ? "text-success" : d < 0 ? "text-critical" : "text-muted")}><ArrowRight size={11} />{d > 0 ? "+" : ""}{d} pts</span>}</td>
            </tr>);
        })}</tbody></table>
    </div>
  );
}

// Compare: each model's status and each table's quality score in Bronze (as uploaded), Silver (cleaned) and Gold (certified).
export default function ReadinessCompare({ data, onRetry }) {
  return (
    <div className="space-y-5 animate-fade-in" data-testid="rd-compare">
      <div className="tile p-4 space-y-2">
        <div className="kpi-label">Bronze vs Silver vs Gold</div>
        <p className="text-[12.5px] text-muted max-w-3xl">Bronze holds every upload as it arrived — repeated uploads, uncleaned codes and unreadable values usually lower its scores, while files not yet promoted can make a model look readier there than in Silver. Silver is what cleaning and review kept; Gold is what was certified.</p>
        <div className="flex flex-wrap gap-4 text-[11.5px] font-mono-tabular text-muted" data-testid="rd-cmp-days">
          {LAYERS.map(l => <span key={l}>{NAME[l]}: {data[l]?.history_days != null ? `${num(data[l].history_days)} days of sales` : "…"}</span>)}</div>
        <BronzeState r={data.bronze} onRetry={onRetry} />
      </div>
      <div className="space-y-2"><div className="kpi-label">Model status by layer</div><Models data={data} /></div>
      <div className="space-y-2"><div className="kpi-label">Data quality by layer</div><Tables data={data} /></div>
      <QualityTrend />
    </div>
  );
}
